import React, { useState, useEffect } from 'react';
import chemicalService from '../../../services/chemicalService';

const UNIT_OPTIONS = ['Kg', 'Liter', 'Gram', 'ML', 'Piece', 'Meter', 'Bag', 'Drum'];
const DEFAULT_GST = 18;

// Read the logged-in username from the JWT stored in localStorage
const getLoggedInUsername = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.username || payload.name || null;
  } catch {
    return null;
  }
};

const blankItem = () => ({
  product_id: '',
  batch_no: '',
  quantity: '',
  unit: 'Kg',
  rate: '',
  expiry_date: '',
  location: '',
});

const InwardFormModal = ({ onClose, onSaved }) => {
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [loadingMaster, setLoadingMaster] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    vendor_id: '',
    purchase_date: new Date().toISOString().split('T')[0],
    bill_no: '',
    gst_percent: DEFAULT_GST,
    // transport
    rider_name: '',
    rider_cnic: '',
    rider_phone: '',
    vehicle_no: '',
    received_by: '',
    // payment
    amount_paid: '',
    payment_mode: 'cash',
    notes: '',
    items: [blankItem()],
  });

  // Load vendors + products
  useEffect(() => {
    const load = async () => {
      try {
        setLoadingMaster(true);
        const [vRes, pRes] = await Promise.all([
          chemicalService.getVendors({ page: 1, limit: 1000 }),
          chemicalService.getProducts({ page: 1, limit: 1000 }),
        ]);
        setVendors((vRes.data || []).filter((v) => v.is_active));
        setProducts((pRes.data || []).filter((p) => p.is_active));
      } catch (err) {
        console.error('Error loading data:', err);
        alert('❌ Failed to load vendors/products');
      } finally {
        setLoadingMaster(false);
      }
    };
    load();
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleItemChange = (index, field, value) => {
    setForm((prev) => {
      const items = [...prev.items];
      items[index] = { ...items[index], [field]: value };
      if (field === 'product_id' && value) {
        const product = products.find((p) => p.id === parseInt(value, 10));
        if (product?.base_unit) items[index].unit = product.base_unit;
      }
      return { ...prev, items };
    });
  };

  const addItem = () => setForm((prev) => ({ ...prev, items: [...prev.items, blankItem()] }));

  const removeItem = (index) => {
    if (form.items.length === 1) { alert('❌ At least one item is required.'); return; }
    setForm((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  };

  // ─── Live totals ──
  const lineAmount = (it) => (parseFloat(it.quantity) || 0) * (parseFloat(it.rate) || 0);
  const subtotal = form.items.reduce((sum, it) => sum + lineAmount(it), 0);
  const gstAmount = subtotal * ((parseFloat(form.gst_percent) || 0) / 100);
  const grandTotal = subtotal + gstAmount;
  const amountPaid = parseFloat(form.amount_paid) || 0;
  const balance = grandTotal - amountPaid;

  const fmtMoney = (n) => 'Rs ' + (Math.round((n || 0) * 100) / 100).toLocaleString('en-PK');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!form.vendor_id) return alert('❌ Select a vendor.');
    if (!form.bill_no.trim()) return alert('❌ Bill number is required.');
    if (!form.purchase_date) return alert('❌ Date is required.');
    if (!form.rider_name.trim()) return alert('❌ Rider name is required.');
    if (!form.vehicle_no.trim()) return alert('❌ Vehicle number is required.');
    if (!form.received_by.trim()) return alert('❌ Received by is required.');

    for (let i = 0; i < form.items.length; i++) {
      const it = form.items[i];
      const idx = i + 1;
      if (!it.product_id) return alert(`❌ Item ${idx}: select a product.`);
      if (!it.batch_no.trim()) return alert(`❌ Item ${idx}: batch number is required.`);
      if (!it.quantity || parseFloat(it.quantity) <= 0) return alert(`❌ Item ${idx}: quantity must be > 0.`);
      if (it.rate === '' || parseFloat(it.rate) < 0) return alert(`❌ Item ${idx}: enter a valid rate.`);
    }

    if (amountPaid > grandTotal) return alert('❌ Amount paid cannot exceed grand total.');

    try {
      setSubmitting(true);
      const payload = {
        bill_no: form.bill_no.trim(),
        vendor_id: parseInt(form.vendor_id, 10),
        purchase_date: form.purchase_date,
        subtotal: Math.round(subtotal * 100) / 100,
        gst_amount: Math.round(gstAmount * 100) / 100,
        gst_percent: parseFloat(form.gst_percent) || 0,
        discount: 0,
        grand_total: Math.round(grandTotal * 100) / 100,
        amount_paid: amountPaid,
        payment_mode: form.payment_mode,
        notes: form.notes.trim() || null,
        // transport
        rider_name: form.rider_name.trim(),
        rider_cnic: form.rider_cnic.trim() || null,
        rider_phone: form.rider_phone.trim() || null,
        vehicle_no: form.vehicle_no.trim(),
        // gate pass extras
        received_by: form.received_by.trim(),
        feed_by: getLoggedInUsername(),
        items: form.items.map((it) => ({
          product_id: parseInt(it.product_id, 10),
          batch_no: it.batch_no.trim(),
          mfg_date: form.purchase_date, // use purchase date as mfg/receipt date
          expiry_date: it.expiry_date || null,
          quantity: parseFloat(it.quantity),
          unit: it.unit,
          rate: parseFloat(it.rate),
          amount: Math.round(lineAmount(it) * 100) / 100,
          location: it.location.trim() || null,
        })),
      };

      const res = await chemicalService.createInward(payload);
      const gpNo = res.data?.gate_pass_no || '';
      alert(`✅ Inward saved! Purchase + stock + gate pass created.${gpNo ? `\nGate Pass: ${gpNo}` : ''}`);
      onSaved();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to save inward';
      alert('❌ ' + msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>

        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>↓ New Inward (Goods Received)</h2>
            <p style={{ color: '#7f8c8d', fontSize: '14px', margin: '4px 0 0 0' }}>
              Records the purchase, stock-in, and gate pass together
            </p>
          </div>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ padding: '24px 30px' }}>

            {/* Transport & vendor */}
            <div style={styles.sectionLabel}>Transport & Vendor</div>
            <div style={styles.row}>
              <div style={{ ...styles.formGroup, flex: 2 }}>
                <label style={styles.label}>Vendor <span style={styles.required}>*</span></label>
                <select
                  value={form.vendor_id}
                  onChange={(e) => handleChange('vendor_id', e.target.value)}
                  style={styles.input}
                  required
                  disabled={loadingMaster}
                >
                  <option value="">
                    {loadingMaster ? 'Loading vendors...' : vendors.length === 0 ? '-- No active vendors --' : '-- Select vendor --'}
                  </option>
                  {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Date <span style={styles.required}>*</span></label>
                <input type="date" value={form.purchase_date} onChange={(e) => handleChange('purchase_date', e.target.value)} style={styles.input} required />
              </div>
            </div>
            <div style={styles.row}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Rider Name <span style={styles.required}>*</span></label>
                <input type="text" value={form.rider_name} onChange={(e) => handleChange('rider_name', e.target.value)} style={styles.input} placeholder="e.g. Akram" />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Vehicle No <span style={styles.required}>*</span></label>
                <input type="text" value={form.vehicle_no} onChange={(e) => handleChange('vehicle_no', e.target.value)} style={styles.input} placeholder="e.g. LEB-1234" />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Received By <span style={styles.required}>*</span></label>
                <input type="text" value={form.received_by} onChange={(e) => handleChange('received_by', e.target.value)} style={styles.input} placeholder="Person who received goods" />
              </div>
            </div>
            <div style={styles.row}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Rider CNIC</label>
                <input type="text" value={form.rider_cnic} onChange={(e) => handleChange('rider_cnic', e.target.value)} style={styles.input} placeholder="optional" />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Rider Phone</label>
                <input type="tel" value={form.rider_phone} onChange={(e) => handleChange('rider_phone', e.target.value)} style={styles.input} placeholder="optional" />
              </div>
            </div>

            {/* Bill details */}
            <div style={styles.sectionLabel}>Bill Details</div>
            <div style={styles.row}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Bill No <span style={styles.required}>*</span></label>
                <input type="text" value={form.bill_no} onChange={(e) => handleChange('bill_no', e.target.value)} style={styles.input} placeholder="e.g. BILL-014" required />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>GST %</label>
                <input type="number" step="0.01" min="0" value={form.gst_percent} onChange={(e) => handleChange('gst_percent', e.target.value)} style={styles.input} />
              </div>
            </div>

            {/* Items */}
            <div style={styles.sectionLabel}>Items Received ({form.items.length})</div>
            <div style={styles.itemsList}>
              {form.items.map((item, index) => (
                <div key={index} style={styles.itemCard}>
                  <div style={styles.itemHeader}>
                    <div style={styles.itemIndex}>{index + 1}</div>
                    <span style={styles.itemTitle}>Item {index + 1}</span>
                    <span style={styles.lineAmount}>{fmtMoney(lineAmount(item))}</span>
                    <button type="button" onClick={() => removeItem(index)} style={styles.removeButton} disabled={form.items.length === 1}>🗑️</button>
                  </div>

                  <div style={styles.itemRow}>
                    <div style={{ flex: 3, minWidth: '180px' }}>
                      <label style={styles.miniLabel}>Product *</label>
                      <select value={item.product_id} onChange={(e) => handleItemChange(index, 'product_id', e.target.value)} style={styles.input} required disabled={loadingMaster}>
                        <option value="">-- Select --</option>
                        {products.map((p) => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
                      </select>
                    </div>
                    <div style={{ flex: 2, minWidth: '130px' }}>
                      <label style={styles.miniLabel}>Batch No *</label>
                      <input type="text" value={item.batch_no} onChange={(e) => handleItemChange(index, 'batch_no', e.target.value)} style={styles.input} placeholder="e.g. CHM-26-014" required />
                    </div>
                    <div style={{ flex: 1, minWidth: '90px' }}>
                      <label style={styles.miniLabel}>Qty *</label>
                      <input type="number" step="0.01" min="0" value={item.quantity} onChange={(e) => handleItemChange(index, 'quantity', e.target.value)} style={styles.input} placeholder="0" required />
                    </div>
                    <div style={{ flex: 1, minWidth: '80px' }}>
                      <label style={styles.miniLabel}>Unit *</label>
                      <select value={item.unit} onChange={(e) => handleItemChange(index, 'unit', e.target.value)} style={styles.input} required>
                        {UNIT_OPTIONS.map((u) => <option key={u} value={u}>{u}</option>)}
                      </select>
                    </div>
                    <div style={{ flex: 1, minWidth: '100px' }}>
                      <label style={styles.miniLabel}>Rate *</label>
                      <input type="number" step="0.01" min="0" value={item.rate} onChange={(e) => handleItemChange(index, 'rate', e.target.value)} style={styles.input} placeholder="0" required />
                    </div>
                  </div>

                  <div style={{ ...styles.itemRow, marginTop: '6px' }}>
                    <div style={{ flex: 1, minWidth: '140px' }}>
                      <label style={styles.miniLabel}>Expiry Date (optional)</label>
                      <input type="date" value={item.expiry_date} onChange={(e) => handleItemChange(index, 'expiry_date', e.target.value)} style={styles.input} />
                    </div>
                    <div style={{ flex: 1, minWidth: '140px' }}>
                      <label style={styles.miniLabel}>Location (optional)</label>
                      <input type="text" value={item.location} onChange={(e) => handleItemChange(index, 'location', e.target.value)} style={styles.input} placeholder="e.g. Warehouse A" />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button type="button" onClick={addItem} style={styles.addItemButton}>+ Add item</button>

            {/* Payment + totals */}
            <div style={styles.sectionLabel}>Payment</div>
            <div style={styles.row}>
              <div style={{ flex: 1, minWidth: '240px' }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Amount Paid Now</label>
                  <input type="number" step="0.01" min="0" value={form.amount_paid} onChange={(e) => handleChange('amount_paid', e.target.value)} style={styles.input} placeholder="0" />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Payment Mode</label>
                  <select value={form.payment_mode} onChange={(e) => handleChange('payment_mode', e.target.value)} style={styles.input}>
                    <option value="cash">Cash</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="cheque">Cheque</option>
                  </select>
                </div>
              </div>

              <div style={styles.totalsBox}>
                <div style={styles.totalRow}><span>Subtotal</span><span>{fmtMoney(subtotal)}</span></div>
                <div style={styles.totalRow}><span>GST ({form.gst_percent || 0}%)</span><span>{fmtMoney(gstAmount)}</span></div>
                <div style={styles.totalRowGrand}><span>Grand Total</span><span>{fmtMoney(grandTotal)}</span></div>
                <div style={styles.totalRowBalance}><span>Balance</span><span>{fmtMoney(balance)}</span></div>
              </div>
            </div>

            {/* Notes */}
            <div style={styles.sectionLabel}>Notes</div>
            <div style={styles.formGroup}>
              <textarea value={form.notes} onChange={(e) => handleChange('notes', e.target.value)} style={{ ...styles.input, minHeight: '50px', resize: 'vertical', fontFamily: 'inherit' }} placeholder="Optional notes..." rows="2" />
            </div>
          </div>

          <div style={styles.modalFooter}>
            <button type="button" onClick={onClose} style={styles.cancelButton} disabled={submitting}>Cancel</button>
            <button type="submit" style={styles.submitButton} disabled={submitting}>
              {submitting ? '⏳ Saving...' : '✓ Save Inward + Purchase'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
  modal: { background: 'white', borderRadius: '12px', width: '95%', maxWidth: '1000px', maxHeight: '92vh', overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' },
  modalHeader: { padding: '20px 30px', borderBottom: '2px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'sticky', top: 0, background: 'white', zIndex: 10 },
  modalTitle: { fontSize: '22px', fontWeight: '700', color: '#2c3e50', margin: 0 },
  closeButton: { padding: '8px 16px', background: '#f0f0f0', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '20px', fontWeight: '700', color: '#666' },
  sectionLabel: { fontSize: '12px', fontWeight: '700', color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '20px', marginBottom: '12px', paddingBottom: '6px', borderBottom: '1px solid #f0f0f0' },
  row: { display: 'flex', gap: '16px', flexWrap: 'wrap' },
  formGroup: { flex: 1, minWidth: '160px', display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '12px' },
  label: { fontSize: '13px', fontWeight: '600', color: '#2c3e50' },
  miniLabel: { fontSize: '11px', fontWeight: '600', color: '#7f8c8d', marginBottom: '4px', display: 'block', textTransform: 'uppercase', letterSpacing: '0.3px' },
  required: { color: '#e74c3c' },
  input: { width: '100%', padding: '10px 12px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', fontFamily: 'inherit' },
  itemsList: { display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '12px' },
  itemCard: { background: '#f8f9fa', borderRadius: '10px', padding: '14px', border: '1px solid #e0e0e0' },
  itemHeader: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px dashed #e0e0e0' },
  itemIndex: { width: '24px', height: '24px', borderRadius: '50%', background: '#3498db', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700', flexShrink: 0 },
  itemTitle: { fontSize: '13px', fontWeight: '700', color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: '0.3px', flex: 1 },
  lineAmount: { fontSize: '14px', fontWeight: '700', color: '#2c3e50' },
  itemRow: { display: 'flex', gap: '10px', flexWrap: 'wrap' },
  removeButton: { padding: '6px 12px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' },
  addItemButton: { width: '100%', padding: '12px', background: 'white', color: '#3498db', border: '2px dashed #3498db', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '700', marginBottom: '12px' },
  totalsBox: { flex: 1, minWidth: '240px', background: '#f8f9fa', borderRadius: '10px', padding: '14px 18px', height: 'fit-content' },
  totalRow: { display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#7f8c8d', padding: '4px 0' },
  totalRowGrand: { display: 'flex', justifyContent: 'space-between', fontSize: '16px', fontWeight: '700', color: '#2c3e50', padding: '8px 0 4px', borderTop: '1px solid #e0e0e0', marginTop: '4px' },
  totalRowBalance: { display: 'flex', justifyContent: 'space-between', fontSize: '14px', fontWeight: '700', color: '#e74c3c', padding: '4px 0' },
  modalFooter: { padding: '16px 30px', borderTop: '2px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end', gap: '12px', position: 'sticky', bottom: 0, background: 'white' },
  cancelButton: { padding: '12px 24px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  submitButton: { padding: '12px 28px', background: '#3498db', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
};

export default InwardFormModal;